(ns hai.spaces.detail
  "Full-page view of a single space: its notes/tasks (add/remove), the people
   it's shared with, and co-owner management."
  (:require
   [clojure.string :as str]
   [hai.entity.data :as entity-data]
   [hai.entity.link-combobox :as link-combobox]
   [hai.entity.sections :as sections]
   [hai.sharing.components :as sharing.components]
   [hai.sharing.helper :as sharing]
   [hai.spaces.data :as data]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent defsub]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; Tracks which spaces a resolve has completed for, so the page can tell "still
;; loading" apart from "not found / no access" — both leave a nil entity.
(defevent ::mark-resolved [db space-uuid]
  (update db ::resolved-uuids (fnil conj #{}) (uuid (str space-uuid))))

(defsub ::resolved?
  [db space-uuid]
  (boolean (and space-uuid
                (contains? (get db ::resolved-uuids #{}) (uuid (str space-uuid))))))

;; ---------------------------------------------------------------------------
;; Styled components
;; ---------------------------------------------------------------------------

(o/defstyled muted :span
  {:color t/--card-forest-70 :font-size "14px"})

(o/defstyled loading-state :div
  :flex :items-center
  {:gap "10px" :padding "8px 2px" :color t/--card-forest-70})

(o/defstyled section :div
  :flex :flex-col
  {:gap "8px" :margin-top "28px"})

(o/defstyled section-heading :div
  :font-semibold
  {:font-size "15px"})

(o/defstyled header :div
  :flex :flex-col
  {:gap "6px"}
  [:.title-row
   :flex :items-center
   {:gap "10px"}
   [:.space-icon
    {:color t/--card-forest-70 :display "inline-flex" :flex-shrink "0"}
    [:svg {:width "28px" :height "28px" :fill "currentColor"}]]
   [:input.title
    :font-semibold
    {:flex "1" :font-size "26px" :border "none" :outline "none"
     :background "transparent" :padding "2px 0"}]]
  [:textarea.description
   :w-100% :resize-none
   {:color t/--card-forest-70 :font-size "15px" :border "none"
    :outline "none" :background "transparent" :min-height "22px"}]
  [:.description-read
   {:color t/--card-forest-70 :font-size "15px"}])

;; ---------------------------------------------------------------------------
;; Views
;; ---------------------------------------------------------------------------

(defn- items-section
  "The space's notes and tasks, with add/remove wired to the space mutations.
   Reuses the note/task link widget (`sections/entity-extras`) — the same one
   the notes and tasks surfaces use to attach existing entities."
  [space can-edit?]
  (r/with-let [!open (r/atom #{})]
    (let [space-uuid (:space/uuid space)]
      [section
       [section-heading "Items"]
       [sections/entity-extras
        {:entity-uuid          space-uuid
         :link-tooltip-text    "Add a note or task to this space"
         :editing?             can-edit?
         :link-urls            []
         :contacts             []
         :linked-notes         (:space/notes space)
         :linked-tasks         (:space/tasks space)
         :open-set             @!open
         :open-section!        #(swap! !open conj %)
         :close-section!       #(swap! !open disj %)
         :on-add-note          (fn [note]
                                 (>e [::data/add-item space-uuid {:note/uuid (:note/uuid note)}]))
         :on-remove-note       (fn [note]
                                 (>e [::data/remove-item space-uuid {:note/uuid (:note/uuid note)}]))
         :on-add-task          (fn [task]
                                 (>e [::data/add-item space-uuid {:task/uuid (:task/uuid task)}]))
         :on-remove-task       (fn [task]
                                 (>e [::data/remove-item space-uuid {:task/uuid (:task/uuid task)}]))
         :on-open-entity       entity-data/open-entity
         :render-entity-search link-combobox/entity-search}]])))

(defn- people-section
  "The space's members — reuses the generic sharing table, which adds/edits
   shares on the space entity itself (read/admin)."
  [space]
  [section
   [section-heading "People"]
   [sharing.components/permissions-table space]])

(defn- co-owners-section
  "Co-owner management: who the hidden space-mgmt entity is shared with, shown in
   the standard shared-with table (read-only — its adds go through the dedicated
   button below), plus an add-side that shares a person that entity *and* admin on
   the space (`space/add-co-owner`). \"Co-owner\" is only a label for a space-mgmt
   share; it is unrelated to the space's `:entity/owner`."
  [space-uuid]
  (r/with-let [_ (>e [::data/load-mgmt space-uuid])]
    (let [mgmt-uuid @(<s [::data/mgmt-uuid space-uuid])]
      [section
       [section-heading "Co-owners"]
       [muted "Co-owners can add/remove people, edit the description and rename the space."]
       (when mgmt-uuid
         [sharing.components/permissions-table {:space-mgmt/uuid mgmt-uuid} {:read-only? true}])
       ;; Searches your contacts (or takes typed emails), like the People box.
       (when mgmt-uuid
         [sharing.components/co-owner-add
          {:id     [::co-owner-add space-uuid]
           :on-add (fn [shares]
                     (doseq [share shares]
                       (>e [::data/add-co-owner space-uuid mgmt-uuid share])))}])])))

(defn- editable-header
  "The space title and description. Editable for admins (saved on blur via
   `space/update`); read-only otherwise. The local drafts are seeded once per
   space — `space-view` is keyed on `:space/uuid`, so a re-resolve after a save
   doesn't clobber what the user typed."
  [space can-edit?]
  (r/with-let [!title (r/atom (:space/title space))
               !desc  (r/atom (or (:space/description space) ""))
               !focused? (atom false)
               ;; A just-created space lands here with the People share input
               ;; auto-focused; steal focus back to the title (and select its
               ;; placeholder-y default) so the writer names the space first.
               ;; setTimeout beats the share input's mount-time autofocus; the
               ;; !focused? / flag-clear pair makes it one-shot.
               attach-title! (fn [el]
                               (when (and el (not @!focused?)
                                          @(<s [::data/just-created? (:space/uuid space)]))
                                 (reset! !focused? true)
                                 (>e [::data/clear-just-created])
                                 (js/setTimeout (fn [] (.focus el) (.select el)) 0)))]
    (let [space-uuid (:space/uuid space)]
      [header
       [:div.title-row
        [:span.space-icon [icons/outlined-workspaces]]
        [:input.title
         {:value       @!title
          :ref         attach-title!
          :placeholder "Space title"
          :disabled    (not can-edit?)
          :on-change   #(reset! !title (.. % -target -value))
          :on-blur     (fn [_]
                         (let [t (str/trim @!title)]
                           (when (and (seq t) (not= t (:space/title space)))
                             (>e [::data/rename space-uuid t]))))}]]
       (cond
         can-edit?
         [:textarea.description
          {:value       @!desc
           :placeholder "Add a description…"
           :rows        2
           :on-change   #(reset! !desc (.. % -target -value))
           :on-blur     (fn [_]
                          (let [d (str/trim @!desc)]
                            (when (not= d (or (:space/description space) ""))
                              (>e [::data/update-description space-uuid d]))))}]

         (not-empty (:space/description space))
         [:div.description-read (:space/description space)])])))

(defn- space-view
  [space]
  (let [space-uuid (:space/uuid space)
        can-edit?  (sharing/can-edit? space)]
    [:<>
     [editable-header space can-edit?]
     [items-section space can-edit?]
     [people-section space]
     (when can-edit?
       [co-owners-section space-uuid])]))

(defn page
  [_route]
  (let [params    @(<s [:routing/path-params])
        space     @(<s [:entity params])
        resolved? @(<s [::resolved? (:space/uuid params)])]
    [layout/page
     [layout/breadcrumbs]
     [layout/content
      (cond
        space     ^{:key (:space/uuid space)} [space-view space]
        resolved? [muted "This space doesn't exist or you no longer have access to it."]
        :else     [loading-state [c/Spinner {:size "3"}] "Loading…"])]]))
